/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import {Injectable} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {DefaultEventsMap} from "@socket.io/component-emitter";
import md5 from 'md5';
import {environment} from '@environments/environment';
import {Socket} from 'socket.io-client';

@Injectable({providedIn: 'root'})
export class SocketIoService {
  private socket: Socket<DefaultEventsMap, DefaultEventsMap> | undefined;
  private secretKey = environment.socket_io.secretKey;
  public joinedAffRooms: Set<string> = new Set(); // Lưu trữ danh sách phòng đã tham gia dùng cho AFF
  private dataChanges = new Subject();
  private userTracking = new Subject();
  private pageTracking = new Subject();
  private ioReconnect = new Subject();
  private mobileNumber = new Subject();
  private recommendProduct = new Subject();
  private tagAndQuickMessage = new Subject();

  constructor() {
    // [DISABLED] UI mới không dùng socket.io nữa — chuyển sang Durable Object (Cloudflare Workers).
    // Giữ lại class để không break các nơi inject SocketIoService, nhưng không tạo kết nối.
    /*
    this.socket = io(environment.socket_io.url, {
    withCredentials: true,
    transports: ['websocket', "polling"]
    });

    const timeout = setTimeout(() => {
    if (!this.socket.connected) {
    this.socket.close();
    }
    }, 10);

    this.socket.on("disconnect", (reason) => {
    if (reason === "io server disconnect") {
    }
    });

    this.socket.on("connect_error", () => {
    this.socket.close();
    });

    this.socket.on('data:changes', data => {
    this.dataChanges.next(data);
    });

    this.socket.on('data:tracking-user', data => {
    this.userTracking.next(data);
    });

    this.socket.on('data:tracking-page', data => {
    this.pageTracking.next(data);
    });

    this.socket.on('mobile-number', data => {
    this.mobileNumber.next(data);
    });

    this.socket.on('data:recommend-product', data => {
    this.recommendProduct.next(data);
    });

    this.socket.io.on('reconnect', reconnectNumber => {
    this.ioReconnect.next(reconnectNumber);
    });

    this.socket.on('data:tag-quick-message', data => {
    this.tagAndQuickMessage.next(data);
    });
    */
  }

  joinRoom(pageIds: string[]): void {
    if (!this.socket) return;
    this.socket.emit('user:join-rooms', {
      rooms: pageIds,
      key: this.getKey()
    });
  }

  leaveRoom(pageIds: string[]): void {
    if (!this.socket) return;
    this.socket.emit('user:leave-rooms', {rooms: pageIds});
  }

  leaveAffRoom(): void {
    const roomsToLeave = Array.from(this.joinedAffRooms);
    if (roomsToLeave.length) {
      this.leaveRoom(roomsToLeave);
      this.joinedAffRooms.clear();
    }
  }

  emitDataChanges(data: any) {
    if (!this.socket) return;
    this.socket.emit('data:changes', {
      roomId: data.roomId,
      data: data.data,
      key: this.getKey(data),
    });
  }

  onDataChanges(): Observable<any> {
    return this.dataChanges;
  }

  emitUserTracking(data: any) {
    if (!this.socket) return;
    this.socket.emit('data:tracking-user', {
      roomId: data.roomId,
      data: data.data,
      key: this.getKey(data),
    });
  }

  onUserTracking(): Observable<any> {
    return this.userTracking;
  }

  emitPageTracking(data: any) {
    if (!this.socket) return;
    this.socket.emit('data:tracking-page', {
      roomId: data.roomId,
      data: data.data,
      key: this.getKey(data),
    });
  }

  onPageTracking(): Observable<any> {
    return this.pageTracking;
  }

  emitMobileTransferData(data: any): void {
    if (!this.socket) return;
    this.socket.emit('mobile:transfer-data', {
      roomId: data.roomId,
      data: data.data,
      key: this.getKey(data),
    });
  }

  onMobileTransferData(): Observable<any> {
    return this.mobileNumber;
  }

  emitChangeRecommendProduct(data: any): void {
    if (!this.socket) return;
    this.socket.emit('data:recommend-product', {
      roomId: data.roomId,
      data: data.data,
      key: this.getKey(data),
    });
  }

  onRecommendProduct(): Observable<any> {
    return this.recommendProduct;
  }

  onReconnect(): Observable<any> {
    return this.ioReconnect;
  }

  emitTagAndQuickMessage(data: any): void {
    if (!this.socket) return;
    this.socket.emit('data:tag-quick-message', {
      roomId: data.roomId,
      data: data.data,
      key: this.getKey(data),
    });
  }

  onTagAndQuickMessage(): Observable<any> {
    return this.tagAndQuickMessage;
  }

  getKey(data: any = null) {
    if (!data) {
      return md5(md5(this.secretKey))
    }
    return md5(md5(JSON.stringify(data.data) + this.secretKey))
  }
}
